import { JsonProperty, Serializable } from 'ts-jackson'

type AnyFn = (...args: never[]) => unknown
export type PickOptional<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>> & Partial<Pick<T, K>>

export type FieldOnly<C> = {
  [K in keyof C as C[K] extends AnyFn ? never : K]: C[K]
}

/**
 * Omit a field from an object, while strongly check if the field exists in the object.
 * The native TypeScript "Omit" type doesn't check this.
 */
export type StrongOmit<T, K extends keyof T> = Omit<T, K>

/**
 * @example
 * type Example = { a: string; b: number; c: boolean };
 * // ValueTypes will be: string | number | boolean
 * type ValueTypes = ExtractValueTypes<Example>;
 */
export type ExtractValueTypes<T> = T extends { [key: string]: infer V } ? V : never

export class Fromable {
  static from<T extends object>(this: new () => T, data: FieldOnly<T>): T {
    return Object.assign(new this(), data)
  }
}

export enum GENDER {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  NOT_SPECIFIED = 'NOT_SPECIFIED',
}

export enum MEMBER_PACKAGE_TYPE {
  ALL = 'ALL',
  MEMBERSHIP = 'MEMBERSHIP',
  PT = 'PT',
  SWIMMING = 'SWIMMING',
}

export interface PaginationOpt {
  page?: number
  pageSize?: number
  query?: string
  /** Facility list only (QTY-1989): true = walk-in allowed, false = not allowed, omit = all. */
  walkIn?: boolean
  roomId?: string | number
  search?: string
  roleIds?: string
  includePresets?: boolean
  packageTypeId?: string | number
  packageType?: MEMBER_PACKAGE_TYPE
  startTime?: string
  start?: string
  endTime?: string
  end?: string
  trainerService?: string | number
  contractType?: string
  classId?: string | number
  saleId?: string | number
  source?: string
  leadStatus?: string | string[]
  startDate?: string
  endDate?: string
  followUpStartDate?: string
  followUpEndDate?: string
  /** Lead / up-sell lists (QTY-2096): only rows overdue on their pending appointment. */
  overdueOnly?: boolean
  /** Lead / up-sell lists (QTY-2096): server-side sort by the pending appointment. */
  sortBy?: 'nextFollowUpAt'
  sortOrder?: 'asc' | 'desc'
  memberStatus?: string
  status?: string
  typeId?: string
  facilityId?: string
  date?: string
}

@Serializable()
export class PageInfo extends Fromable {
  @JsonProperty('page')
  page!: number

  @JsonProperty('total')
  total!: number

  @JsonProperty('pageSize')
  pageSize!: number
}

export interface PaginationResponse<T> {
  pagination: PageInfo
  data: T[]
}
export interface PaginationResponseWithTotals<T> {
  pagination: PageInfo
  data: T[]
  totalMembershipSales?: number | null
  totalPersonalTrainerSales?: number | null
  totalSales?: number | null
  totalSwimmingSales?: number | null
}

@Serializable()
export class Role extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('group')
  group!: string

  @JsonProperty('position')
  position!: string

  public option() {
    return { label: this.name, value: this.id }
  }
}
